import { useEffect, useMemo } from 'react';
import { useModeloApresentacao } from '../..';
import { useConfig } from '../../config/config.hook';
import { Modelo } from '../../config/internal/modelo-apresentacao/modelo-apresentacao.types';
import { useMatricula } from '../../matricula/matricula.hook';
import { useTurmaSelecionada } from '../../turmas';
import { PerfilAluno, PerfilEducador, useUsuario, Usuario } from '../../usuario';
import { TipoEvento } from '../datalayer.types';
import { imprimirLog } from '../imprimir-log.datalayer';
import { PageViewSupplier } from './datalayer-event-page.types';

interface PageView {
  event: TipoEvento;
  modeloApresentacao: Modelo;
  [key: string]: string;
}

export const useDataLayerEventPage = (): PageViewSupplier => {
  const { brand } = useConfig();
  const { modelo } = useModeloApresentacao();
  const { turmaSelecionada } = useTurmaSelecionada();

  const usuario = useUsuario();
  const matricula = useMatricula();

  const pageViewDefaultFields = useMemo<PageView>(
    () => ({
      event: 'page',
      modeloApresentacao: modelo,
    }),
    [modelo],
  );

  useEffect(() => {
    if (usuario.get()) {
      const usuarioLogado = usuario.get() as Usuario<PerfilAluno | PerfilEducador>;

      pageViewDefaultFields['dimension1'] = usuarioLogado.aura.dados.id;
      pageViewDefaultFields['dimension2'] = matricula.getSelecionada() ? 'aluno' : 'educador';
      pageViewDefaultFields['dimension3'] = brand;
    }

    if (matricula.getSelecionada()) {
      pageViewDefaultFields['nomeCurso'] = matricula.getSelecionada().nomeCurso;
      pageViewDefaultFields['tipoCurso'] = matricula.getSelecionada().tipoCurso;
      pageViewDefaultFields['turnoCurso'] = matricula.getSelecionada().turno;
      pageViewDefaultFields['campusCurso'] = matricula.getSelecionada().campus;
      pageViewDefaultFields['modalidade'] = matricula.getSelecionada().modalidade;
      pageViewDefaultFields['matricula'] = matricula.getSelecionada().id;
      pageViewDefaultFields['codigoCurso'] = matricula.getSelecionada().codigoCurso;
      pageViewDefaultFields['cd44'] = matricula.getSelecionada().periodo;

      if (matricula.getSelecionada().matricula) {
        pageViewDefaultFields['codigoMatricula'] = `${brand}_${matricula.getSelecionada().matricula}`;
      }
    }
  }, [matricula, usuario, brand, pageViewDefaultFields]);

  useEffect(() => {
    if (!turmaSelecionada) {
      pageViewDefaultFields['idEntrega'] = undefined;
      pageViewDefaultFields['nomeDisciplinaTurma'] = undefined;
      pageViewDefaultFields['codDisciplina'] = undefined;
      pageViewDefaultFields['codTurma'] = undefined;
      pageViewDefaultFields['formatoEntrega'] = undefined;
      pageViewDefaultFields['turmaAtual'] = undefined;
      pageViewDefaultFields['idTema'] = undefined;
      pageViewDefaultFields['nomeTema'] = undefined;
      pageViewDefaultFields['numeroTema'] = undefined;
    } else {
      pageViewDefaultFields['idEntrega'] = turmaSelecionada.id;
      pageViewDefaultFields['nomeDisciplinaTurma'] = turmaSelecionada.disciplina.nome;
      pageViewDefaultFields['codDisciplina'] = turmaSelecionada.disciplina.codigo;
      pageViewDefaultFields['codTurma'] = turmaSelecionada.codigoEntrega;
      pageViewDefaultFields['formatoEntrega'] = turmaSelecionada.formato;
    }
  }, [pageViewDefaultFields, turmaSelecionada]);

  return {
    push: () => {
      imprimirLog({ tipoEvento: 'page', dados: pageViewDefaultFields });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any)?.dataLayer?.push(pageViewDefaultFields);
    },
    add: function (entry: { key: string; value: string }) {
      pageViewDefaultFields[entry.key] = entry.value;
      return this;
    },
    remove: function (entry: { key: string }) {
      delete pageViewDefaultFields[entry.key];
      return this;
    },
  };
};
